<template>
  <v-container class="mt-4">
    <v-progress-linear v-if="isLoading" indeterminate color="primary"></v-progress-linear>

    <div class="d-flex flex-column align-center mt-4 text-center">
      <h2>Regel-Texte &amp; Grenzwerte</h2>
      <p class="text-medium-emphasis mt-2">
        Hier können alle Meldungstexte der Regelprüfung sowie die festen Grenzwerte angepasst werden.
      </p>
    </div>

    <v-alert type="info" variant="tonal" class="my-4" density="comfortable">
      Platzhalter in geschweiften Klammern werden automatisch ersetzt, z. B.
      <code>{max}</code> (Grenzwert), <code>{date}</code> (Datum), <code>{weeks}</code> (Wochen-Zeitfenster).
    </v-alert>

    <div class="d-flex justify-end mb-4" style="gap: 8px">
      <v-btn variant="outlined" color="primary" @click="ResetToDefaults">Standard wiederherstellen</v-btn>
      <v-btn color="primary" :loading="isSaving" @click="Save">Speichern</v-btn>
    </div>

    <template v-if="ruleConfig">
      <v-card class="mb-6" variant="outlined">
        <v-card-title>Grenzwerte</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="3">
              <v-text-field
                  label="Zeitfenster (Wochen)"
                  type="number"
                  hint="Erscheint als {weeks} in Texten"
                  persistent-hint
                  v-model.number="ruleConfig.thresholds.weekWindow"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                  label="Freie-Tage-Fenster (Wochen)"
                  type="number"
                  hint="Erscheint als {freeWeeks} in Texten"
                  persistent-hint
                  v-model.number="ruleConfig.thresholds.freeDaysWeekWindow"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                  label="Mindesturlaub (Tage)"
                  type="number"
                  hint="Erscheint als {minDays} in Texten"
                  persistent-hint
                  v-model.number="ruleConfig.thresholds.vacationMinDays"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                  label="Maximalurlaub (Tage)"
                  type="number"
                  hint="Erscheint als {maxDays} in Texten"
                  persistent-hint
                  v-model.number="ruleConfig.thresholds.vacationMaxDays"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-card class="mb-6" variant="outlined">
        <v-card-title>Hinweistexte</v-card-title>
        <v-card-text>
          <p class="text-medium-emphasis mb-4">
            Diese Texte erscheinen auf der Startseite sowie in den Info-Fenstern der Urlaubs- und
            Dienstwünsche. Einfache HTML-Formatierung (z. B. <code>&lt;p&gt;</code>, <code>&lt;b&gt;</code>,
            <code>&lt;strong&gt;</code>) ist erlaubt.
          </p>
          <div
              v-for="def in infoTextDefinitions"
              :key="def.key"
              class="mb-6"
          >
            <div class="d-flex align-center flex-wrap mb-1" style="gap: 8px">
              <strong>{{ def.label }}</strong>
              <v-chip
                  v-for="ph in def.placeholders"
                  :key="ph"
                  size="x-small"
                  color="primary"
                  variant="tonal"
              >{{ ph }}</v-chip>
            </div>
            <v-textarea
                auto-grow
                rows="4"
                density="compact"
                v-model="ruleConfig.infoTexts[def.key]"
            ></v-textarea>
          </div>
        </v-card-text>
      </v-card>

      <v-card v-for="group in groups" :key="group" class="mb-6" variant="outlined">
        <v-card-title>{{ group }}</v-card-title>
        <v-card-text>
          <div
              v-for="def in definitionsByGroup(group)"
              :key="def.key"
              class="mb-6"
          >
            <div class="d-flex align-center flex-wrap mb-1" style="gap: 8px">
              <strong>{{ def.label }}</strong>
              <v-chip
                  v-for="ph in def.placeholders"
                  :key="ph"
                  size="x-small"
                  color="primary"
                  variant="tonal"
              >{{ ph }}</v-chip>
            </div>
            <v-text-field
                label="Meldung (ausführlich)"
                density="compact"
                v-model="ruleConfig.messages[def.key].message"
            ></v-text-field>
            <v-text-field
                label="Kurztext"
                density="compact"
                hide-details
                v-model="ruleConfig.messages[def.key].shortMessage"
            ></v-text-field>
          </div>
        </v-card-text>
      </v-card>
    </template>
  </v-container>
</template>

<script lang="ts">
import {computed, defineComponent, onMounted, ref} from "vue";
import {notifyError, notifySuccess} from "vuetify3-dialog";
import {
  GetRuleConfig,
  SetRuleConfig,
  createDefaultRuleConfig,
  ruleMessageDefinitions,
  RuleMessageDefinition,
  infoTextDefinitions,
} from "@/data/ruleConfig";
import {loadRuleConfig} from "@/rules/ruleEngine";

export default defineComponent({
  name: "RuleTextsView",
  setup() {
    const isLoading = ref(false);
    const isSaving = ref(false);
    const ruleConfig = ref<RuleConfig | null>(null);

    const groups = computed(() => {
      const seen: string[] = [];
      ruleMessageDefinitions.forEach((def) => {
        if (!seen.includes(def.group)) seen.push(def.group);
      });
      return seen;
    });

    const definitionsByGroup = (group: string): RuleMessageDefinition[] =>
        ruleMessageDefinitions.filter((def) => def.group === group);

    // Stellt sicher, dass für jede bekannte Regel ein Text-Objekt existiert
    const ensureMessages = (config: RuleConfig) => {
      const defaults = createDefaultRuleConfig();
      ruleMessageDefinitions.forEach((def) => {
        if (!config.messages[def.key]) {
          config.messages[def.key] = { ...defaults.messages[def.key] };
        }
      });
      if (!config.infoTexts) {
        config.infoTexts = { ...defaults.infoTexts };
      }
    };

    const load = async () => {
      isLoading.value = true;
      try {
        const config = await GetRuleConfig();
        ensureMessages(config);
        ruleConfig.value = config;
      } catch (error) {
        console.error("Error loading rule config:", error);
        await notifyError("Regel-Texte konnten nicht geladen werden.");
        ruleConfig.value = createDefaultRuleConfig();
      } finally {
        isLoading.value = false;
      }
    };

    const Save = async () => {
      if (!ruleConfig.value) return;
      isSaving.value = true;
      try {
        await SetRuleConfig(ruleConfig.value);
        loadRuleConfig(ruleConfig.value);
        await notifySuccess("Regel-Texte gespeichert.");
      } catch (error) {
        console.error("Error saving rule config:", error);
        await notifyError("Speichern fehlgeschlagen.");
      } finally {
        isSaving.value = false;
      }
    };

    const ResetToDefaults = () => {
      ruleConfig.value = createDefaultRuleConfig();
    };

    onMounted(load);

    return {isLoading, isSaving, ruleConfig, groups, definitionsByGroup, infoTextDefinitions, Save, ResetToDefaults};
  },
});
</script>

<style scoped>
code {
  background: rgba(0, 0, 0, 0.06);
  padding: 1px 5px;
  border-radius: 3px;
}
</style>
